import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { LoadingButton } from "@mui/lab";
import { Seccion, Rejilla, fieldSx, contenidoSx, pieSx, botonPrincipalSx } from "../../../components/DialogoUi";

export default function EditCoordinacionDialog({
  open,
  onClose,
  onSave,
  saving,
  cod,
  coordId,
  estadoInfo,
  form,
  setForm,
  phoneValid,
  placas,
  setPlacas,
  placaInput,
  setPlacaInput,
  invalids,
  tecnicos,
  tecnicosSel,
  setTecnicosSel,
  esAdicion,
  tieneEquiposAsignados = false,
  tercializado,
  setTercializado,
  tercOptions,
  tercLoading,
  tercSelected,
  setTercSelected,
  tercError,
  setTercError,
  fetchTercializados,
  requierePlacasCliente,
  placasCliente,
  placasClienteLoading,
  buscarPlacasCliente,
  tipo_requerimiento,
  razon_social,
  descripcion,
  // nueva prop, con default para no afectar otros usos
  loadingReq = false,
}) {
  const estadoNum = React.useMemo(() => {
    if (!estadoInfo) return NaN;
    const m = String(estadoInfo.label || "").match(/(\d+)/);
    if (m && m[1]) return Number(m[1]);
    return NaN;
  }, [estadoInfo]);

  const isBloqueado = !isNaN(estadoNum) && estadoNum >= 7;
  const normalizePlate = (value) =>
    (value || "").trim().replace(/\s+/g, "").toUpperCase();
  const getPlate = (option) =>
    normalizePlate(typeof option === "string" ? option : option?.placa);
  const getClientesText = (option) =>
    Array.isArray(option?.clientes) ? option.clientes.filter(Boolean).join(", ") : "";

  useEffect(() => {
    // Si no es Adición, apaga y limpia todo lo de “tercializado”
    if (!esAdicion) {
      if (tercializado) setTercializado(false);
      if (tercSelected) setTercSelected(null);
      if (tercError) setTercError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAdicion]);

  const cliente = [razon_social, descripcion].filter(Boolean).join(" ") || "—";
  const setCampo = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 1 } }}>
      {/* Encabezado: qué requerimiento se edita y en qué estado está */}
      <DialogTitle sx={{ py: 1.25, borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25 }}>Editar coordinación</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>Req #{cod}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }} noWrap title={cliente}>{cliente}</Typography>
        <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Chip size="small" color="primary" label={estadoInfo?.label || "—"} sx={{ height: 22, fontSize: 11.5, fontWeight: 700 }} />
          {tipo_requerimiento && (
            <Chip size="small" color="info" variant="outlined" label={tipo_requerimiento} sx={{ height: 22, fontSize: 11.5, fontWeight: 700 }} />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={contenidoSx}>
        {loadingReq ? (
          <Box sx={{ py: 4, display: "flex", gap: 2, alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={30} />
            <Typography variant="body2">Cargando datos del requerimiento…</Typography>
          </Box>
        ) : (
          <>
            {isBloqueado && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Este requerimiento ya está en un estado en que la coordinación no se puede modificar. Solo se puede consultar.
              </Alert>
            )}

            <Seccion titulo="Cuándo y dónde">
              <Rejilla cols={3}>
                <TextField
                  type="date" label="Fecha" size="small" fullWidth required sx={fieldSx}
                  value={form.fecha} onChange={setCampo("fecha")}
                  InputLabelProps={{ shrink: true }} error={!form.fecha}
                />
                <TextField
                  type="time" label="Hora" size="small" fullWidth required sx={fieldSx}
                  value={form.hora} onChange={setCampo("hora")}
                  InputLabelProps={{ shrink: true }} error={!form.hora}
                />
                <TextField
                  label="Lugar" size="small" fullWidth sx={fieldSx}
                  value={form.lugar} onChange={setCampo("lugar")}
                  placeholder="Dirección o punto de encuentro"
                />
              </Rejilla>
            </Seccion>

            <Seccion titulo="Contacto en el lugar">
              <Rejilla cols={2}>
                <TextField
                  label="Nombre del contacto" size="small" fullWidth sx={fieldSx}
                  value={form.contacto} onChange={setCampo("contacto")}
                />
                <TextField
                  label="Teléfono" size="small" type="tel" fullWidth sx={fieldSx}
                  value={form.telefono} onChange={setCampo("telefono")}
                  error={!!form.telefono && !phoneValid}
                  helperText={!!form.telefono && !phoneValid ? "Teléfono inválido" : ""}
                />
              </Rejilla>
            </Seccion>

            <Seccion
              titulo="Placas"
              nota={`${placas.length} placa${placas.length === 1 ? "" : "s"}${invalids.length ? ` · ${invalids.length} inválida${invalids.length === 1 ? "" : "s"}` : ""}`}
            >
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  freeSolo={!requierePlacasCliente}
                  options={requierePlacasCliente ? placasCliente : []}
                  loading={requierePlacasCliente && placasClienteLoading}
                  getOptionLabel={(option) => getPlate(option)}
                  isOptionEqualToValue={(option, selected) => getPlate(option) === getPlate(selected)}
                  noOptionsText={
                    requierePlacasCliente
                      ? placasClienteLoading
                        ? "Cargando placas del cliente…"
                        : "Sin placas registradas para este cliente"
                      : "Escribe una placa y presiona Enter"
                  }
                  value={placas}
                  onChange={(_, newValue) => {
                    const normalized = (newValue || [])
                      .map(getPlate)
                      .filter(Boolean);

                    setPlacas(Array.from(new Set(normalized)));
                  }}
                  inputValue={placaInput}
                  onInputChange={(_, newInput, reason) => {
                    setPlacaInput(newInput);
                    if (requierePlacasCliente && reason === "input") {
                      buscarPlacasCliente?.(newInput);
                    }
                  }}
                  filterSelectedOptions
                  renderOption={(props, option) => {
                    const plate = getPlate(option);
                    const clientes = getClientesText(option);
                    return (
                      <li {...props} key={plate}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {plate}
                          </Typography>
                          {clientes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {clientes}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        sx={{ height: 22, fontWeight: 700, fontSize: 11.5 }}
                        color={
                          /^[A-Z]{2,4}-\d{3,4}|\d{3,4}-[A-Z]{2,4}$/.test(option) // /^[A-Z0-9\-\/]{4,12}$/    para mostrar todo verde
                            ? "primary"
                            : "error"
                        }
                      />
                    ))
                  }
                  renderInput={(params) => {
                    const helper = requierePlacasCliente
                      ? placasClienteLoading
                        ? "Cargando placas del cliente…"
                        : placasCliente.length === 0
                        ? "No hay placas registradas para este servicio."
                        : invalids.length > 0
                        ? `Placas inválidas: ${invalids.join(", ")}`
                        : "Selecciona únicamente placas registradas del cliente."
                      : invalids.length > 0
                      ? `Placas inválidas: ${invalids.join(", ")}`
                      : "Puedes pegar varias separadas por coma, espacio o salto de línea.";

                    const prevOnKeyDown = params.inputProps.onKeyDown;
                    const prevOnPaste = params.inputProps.onPaste;

                    return (
                      <TextField
                        {...params}
                        size="small"
                        sx={fieldSx}
                        label="Placas"
                        placeholder={
                          requierePlacasCliente
                            ? "Elige placas del cliente…"
                            : "Escribe placa y Enter/Coma/Espacio…"
                        }
                        error={requierePlacasCliente && invalids.length > 0}
                        helperText={helper}
                        onKeyDown={(e) => {
                          if (requierePlacasCliente) return;
                          prevOnKeyDown?.(e);
                          if (["Enter", ",", " "].includes(e.key)) {
                            e.preventDefault();
                            const val = (placaInput || "")
                              .trim()
                              .replace(/\s+/g, "")
                              .toUpperCase();
                            if (val)
                              setPlacas((prev) =>
                                Array.from(new Set([...prev, val]))
                              );
                            setPlacaInput("");
                          }
                        }}
                        onPaste={(e) => {
                          if (requierePlacasCliente) return;
                          prevOnPaste?.(e);
                          const text = e.clipboardData?.getData("text");
                          if (text) {
                            e.preventDefault();
                            const items = text
                              .split(/[\s,;\n\r]+/g)
                              .map((s) =>
                                (s || "")
                                  .trim()
                                  .replace(/\s+/g, "")
                                  .toUpperCase()
                              )
                              .filter(Boolean);
                            setPlacas((prev) =>
                              Array.from(new Set([...prev, ...items]))
                            );
                            setPlacaInput("");
                          }
                        }}
                        onBlur={() => {
                          if (requierePlacasCliente) {
                            setPlacaInput("");
                            return;
                          }
                          const val = (placaInput || "")
                            .trim()
                            .replace(/\s+/g, "")
                            .toUpperCase();
                          if (val)
                            setPlacas((prev) =>
                              Array.from(new Set([...prev, val]))
                            );
                          setPlacaInput("");
                        }}
                      />
                    );
                  }}
                />
                {!!placas.length && (
                  <Tooltip title="Quitar todas las placas">
                    <IconButton size="small" onClick={() => setPlacas([])} sx={{ mt: 0.25 }}>
                      <BackspaceIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Seccion>

            <Seccion titulo="Técnicos" nota={tecnicosSel.length ? `${tecnicosSel.length} asignado${tecnicosSel.length === 1 ? "" : "s"}` : "sin asignar"}>
              <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel id="tecnicos-label">Técnicos</InputLabel>
                <Select
                  labelId="tecnicos-label"
                  label="Técnicos"
                  multiple
                  value={tecnicosSel}
                  onChange={(e) => setTecnicosSel(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.25 }}>
                      {selected.map((username) => {
                        const t = tecnicos.find((x) => x.username === username);
                        return (
                          <Chip key={username} label={t ? t.nombre : username} size="small" color="primary" sx={{ height: 22, fontSize: 11.5 }} />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                >
                  {tecnicos.map((t) => {
                    const selected = tecnicosSel.indexOf(t.username) > -1;
                    return (
                      <MenuItem key={t.username} value={t.username} dense>
                        <Checkbox checked={selected} size="small" />
                        <ListItemText primary={t.nombre} secondary={t.sucursal} sx={{ ml: 1 }} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Seccion>

            {/* Solo las Adiciones pueden tener cliente tercializado */}
            {esAdicion && (
              <Seccion titulo="Cliente tercializado" nota="solo para Adición">
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "auto minmax(0, 1fr)" }, gap: 1, alignItems: "center" }}>
                  <Tooltip title={tieneEquiposAsignados ? "No se puede cambiar: ya se adicionaron equipos a este requerimiento" : ""}>
                    <span>
                      <FormControlLabel
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                        control={
                          <Checkbox
                            size="small"
                            checked={tercializado}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setTercializado(checked);
                              if (!checked) {
                                setTercSelected(null);
                                setTercError(false);
                              } else {
                                fetchTercializados("");
                              }
                            }}
                            disabled={tieneEquiposAsignados}
                          />
                        }
                        label={tieneEquiposAsignados ? "Es tercializado (bloqueado: ya hay equipos adicionados)" : "Es para un cliente tercializado"}
                      />
                    </span>
                  </Tooltip>

                  {tercializado && (
                    <Autocomplete
                      size="small"
                      options={tercOptions}
                      filterOptions={(x) => x}
                      loading={tercLoading}
                      value={tercSelected}
                      onOpen={() => {
                        if (!tercOptions.length && !tercLoading)
                          fetchTercializados("");
                      }}
                      onChange={(_, val) => {
                        setTercSelected(val);
                        if (val) setTercError(false);
                      }}
                      onInputChange={(_, val, reason) => {
                        if (reason === "input" && (!val || val.length >= 2))
                          fetchTercializados(val);
                      }}
                      getOptionLabel={(o) =>
                        o?.razon_social ? `${o.razon_social}` : ""
                      }
                      isOptionEqualToValue={(o, v) =>
                        String(o.cod_servicio) === String(v.cod_servicio)
                      }
                      noOptionsText={tercLoading ? "Cargando..." : "Sin coincidencias"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={fieldSx}
                          label="Cliente tercializado"
                          placeholder="Razón social / descripción / sucursal"
                          required
                          error={tercError}
                          helperText={tercError ? "Selecciona un cliente tercializado." : ""}
                        />
                      )}
                      ListboxProps={{ style: { maxHeight: 260 } }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.cod_servicio}
                          style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <b style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {option.razon_social}
                            </b>
                            <Typography component="small" variant="caption" color="text.secondary">
                              {option.descripcion} • {option.tipo} • {option.sucursal}
                            </Typography>
                          </Box>
                          <Chip size="small" label={`#${option.cod_servicio}`} />
                        </li>
                      )}
                    />
                  )}
                </Box>
              </Seccion>
            )}

            <Seccion titulo="Observación">
              <TextField
                value={form.observacion}
                onChange={setCampo("observacion")}
                fullWidth
                multiline
                minRows={3}
                size="small"
                sx={fieldSx}
                placeholder="Describe el trabajo o deja una nota de la coordinación"
              />
            </Seccion>
          </>
        )}
      </DialogContent>

      <DialogActions sx={pieSx}>
        <Button onClick={onClose} disabled={saving || loadingReq} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={onSave}
          disabled={saving || isBloqueado || loadingReq}
          sx={botonPrincipalSx}
        >
          Guardar cambios
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
